type CategoryValue = 'all' | 'orders' | 'inventory' | 'partners' | 'connectivity';

type ScheduleValue =
    | 'Sent hourly'
    | 'Sent in real time'
    | 'Sent daily'
    | 'Configurable from the Scheduled Reports page'
    | 'Sent every 6 hours';

type EventTypeInfo = {
    id: number;
    description: string;
    category: CategoryValue;
    schedule: ScheduleValue;
    details: string;
    scheduled?: boolean;
    stageOnly?: boolean;
};

type EventTypesShape = {
    Info: EventTypeInfo[];
    Error: EventTypeInfo[];
    Warning: EventTypeInfo[];
};

const Categories = {
    All: 'all' as CategoryValue,
    Orders: 'orders' as CategoryValue,
    Inventory: 'inventory' as CategoryValue,
    Partners: 'partners' as CategoryValue,
    Connectivity: 'connectivity' as CategoryValue
}

const Schedules = {
    Hourly: 'Sent hourly' as ScheduleValue,
    RealTime: 'Sent in real time' as ScheduleValue,
    Daily: 'Sent daily' as ScheduleValue,
    Configurable: 'Configurable from the Scheduled Reports page' as ScheduleValue,
    SixHours: 'Sent every 6 hours' as ScheduleValue
}

const EventTypes: EventTypesShape = {
    Info: [
        {
            id: 3,
            description: "New order",
            category: Categories.Orders,
            schedule: Schedules.Hourly,
            details: "Notifies retailers of the first order exchanged between partners and all sales orders received in the past hour. Notifies suppliers of all new purchase orders received in the past hour. Includes a link to view them in the Portal."
        },
        {
            id: 4,
            description: "New shipment",
            category: Categories.Orders,
            schedule: Schedules.Hourly,
            details: "Alerts for all new shipments received in the past hour. Includes a link to view them in the Portal."
        },
        {
            id: 20,
            description: "New invoice",
            category: Categories.Orders,
            schedule: Schedules.Hourly,
            details: "Alerts for all new invoices received in the past hour. Includes a link to view them in the Portal."
        },
        {
            id: 52,
            description: "New return",
            category: Categories.Orders,
            schedule: Schedules.Hourly,
            details: "Alerts for all new returns received in the past hour. Includes a link to view them in the Portal."
        },
        {
            id: 29,
            description: "Order cancellation",
            category: Categories.Orders,
            schedule: Schedules.RealTime,
            details: "Alerts for cancelled orders."
        },
        {
            id: 19,
            description: "Monitoring alert",
            scheduled: true,
            category: Categories.Orders,
            schedule: Schedules.Configurable,
            details: "Sends reports based on thresholds set in the Scheduled Reports page."
        },
        {
            id: 23,
            description: "Daily activity summary",
            scheduled: true,
            category: Categories.Orders,
            schedule: Schedules.Configurable,
            details: "Summarizes daily totals of transmitted documents: Orders, Shipments, Invoices, Open/Completed statuses."
        },
        {
            id: 24,
            description: "Monthly document count",
            scheduled: true,
            category: Categories.Orders,
            schedule: Schedules.Configurable,
            details: "Summarizes all document activity for the month. Includes counts for orders, shipments, and invoices."
        },
        {
            id: 25,
            description: "Monthly order count",
            scheduled: true,
            category: Categories.Orders,
            schedule: Schedules.Configurable,
            details: "Provides a total count of orders received during the month."
        },
        {
            id: 31,
            description: "FTP activity (file upload/delete)",
            category: Categories.Connectivity,
            schedule: Schedules.RealTime,
            details: "Tracks all SFTP/FTP activity (uploads, deletes, edits) with metadata (user, company ID, IP, path) used to verify logins and downloads."
        },
        {
            id: 37,
            description: "Unknown EDI documents",
            category: Categories.Connectivity,
            schedule: Schedules.RealTime,
            details: "Alerts when an unrecognized type of EDI document is received."
        },
        {
            id: 30,
            description: "Failed document report",
            scheduled: true,
            category: Categories.Connectivity,
            schedule: Schedules.SixHours,
            details: "Lists all documents (Orders, Acks, Shipments, Invoices) in \"Failed\" status."
        },
        {
            id: 40,
            description: "Inventory update report",
            scheduled: true,
            category: Categories.Inventory,
            schedule: Schedules.Daily,
            details: "Daily summary of inventory updates from all partners. Includes matched/unmatched items and in-stock %."
        },
        {
            id: 41,
            description: "Unmatched inventory report",
            scheduled: true,
            category: Categories.Inventory,
            schedule: Schedules.Daily,
            details: "Lists unmatched SKUs across partners."
        },
        {
            id: 39,
            description: "Inventory activity",
            category: Categories.Inventory,
            schedule: Schedules.RealTime,
            details: "Alerts when a partner uploads inventory. Includes item count and file download link."
        },
        {
            id: 47,
            description: "Document aging report",
            scheduled: true,
            category: Categories.Orders,
            schedule: Schedules.Configurable,
            details: "Lists all open documents by partner with age brackets: <7, 7-14, 14-30, 30-60, >60 days."
        },
        {
            id: 56,
            description: "Trading partner notification",
            category: Categories.Partners,
            schedule: Schedules.RealTime,
            details: "Alerts for partner messages (via Messaging Center or EDI 864), non-standard documents and all On-Demand Onboarding notifications (supplier completed form and requests to go live)."
        },
        {
            id: 58,
            description: "Scorecard",
            scheduled: true,
            category: Categories.Partners,
            schedule: Schedules.Configurable,
            details: "Weekly scorecard for all partners, including scores, ship times, fulfillment %, and cancel %."
        },
        {
            id: 62,
            description: "Routing instructions",
            category: Categories.Orders,
            schedule: Schedules.RealTime,
            details: "Notifies when routing instructions (EDI 754) are received. Includes reference numbers, pickup times, carrier, and contact info."
        },
        {
            id: 65,
            description: "GDPR notification",
            category: Categories.Partners,
            schedule: Schedules.RealTime,
            details: "Provides updates on redacted documents related to your account."
        },
        {
            id: 66,
            description: "Testing completed",
            stageOnly: true,
            scheduled: false,
            category: Categories.Partners,
            schedule: Schedules.RealTime,
            details: "Notifies when test cases are completed for a trading partner."
        },
        {
            id: 60,
            description: "Product feed notification",
            category: Categories.Inventory,
            schedule: Schedules.RealTime,
            details: "Alerts when a product feed is received and is ready for the partner to review."
        },
        {
            id: 71,
            description: "Catalog notification",
            category: Categories.Inventory,
            schedule: Schedules.RealTime,
            details: "Alerts when a product catalog is received through product onboarding center and is ready for the partner to review."
        },
        {
            id: 74,
            description: "Custom report",
            scheduled: true,
            category: Categories.Orders,
            schedule: Schedules.Configurable,
            details: "Custom document report using an export template."
        }
    ],
    Error: [
        {
            id: 2,
            description: "EDI document rejected",
            category: Categories.Connectivity,
            schedule: Schedules.RealTime,
            details: "Notifies when an EDI document is rejected due to format or partner ID issues."
        },
        {
            id: 10,
            description: "SKU not found",
            category: Categories.Inventory,
            schedule: Schedules.RealTime,
            details: "Alerts when a document references a SKU not found in your system."
        },
        {
            id: 6,
            description: "Order sourcing failed",
            category: Categories.Orders,
            schedule: Schedules.RealTime,
            details: "Triggered when Logicbroker cannot source a supplier for a sales order using PO routing."
        },
        {
            id: 63,
            description: "Business rule failed",
            category: Categories.Orders,
            schedule: Schedules.RealTime,
            details: "Triggered when a custom business rule fails to run (usually involving complex validations)."
        },
        {
            id: 13,
            description: "Failed to update shipping information",
            category: Categories.Orders,
            schedule: Schedules.RealTime,
            details: "Triggered when shipment updates fail due to mismatched items."
        },
        {
            id: 16,
            description: "Tracking upload failed",
            category: Categories.Orders,
            schedule: Schedules.RealTime,
            details: "Alerts when a bulk tracking file (CSV/XLSX) upload from the Orders page fails. Includes failure details."
        },
        {
            id: 32,
            description: "External XML translation alert",
            category: Categories.Connectivity,
            schedule: Schedules.RealTime,
            details: "Triggered when custom XML translation fails."
        },
        {
            id: 49,
            description: "Flat file translation alert",
            category: Categories.Connectivity,
            schedule: Schedules.RealTime,
            details: "Alerts when a flat file fails to translate into a Logicbroker-supported format."
        },
        {
            id: 38,
            description: "Inventory error",
            category: Categories.Inventory,
            schedule: Schedules.RealTime,
            details: "Triggered when an inventory upload fails. Includes download link and error reason."
        },
        {
            id: 57,
            description: "API error",
            category: Categories.Connectivity,
            schedule: Schedules.RealTime,
            details: "Triggered when Logicbroker fails to connect via API to your system."
        },
        {
            id: 64,
            description: "Scheduled task failed",
            category: Categories.Orders,
            schedule: Schedules.RealTime,
            details: "Triggered when a scheduled job fails, e.g., file pickup from FTP or scheduled inventory update."
        },
        {
            id: 51,
            description: "Document validation failed",
            category: Categories.Orders,
            schedule: Schedules.RealTime,
            details: "Triggered when a document fails validation based on your or your partner's rules."
        },
        {
            id: 59,
            description: "Product feed failure",
            category: Categories.Inventory,
            schedule: Schedules.RealTime,
            details: "Notifies when a product feed fails to process due to the data not being compliant."
        },
        {
            id: 70,
            description: "Catalog failure",
            category: Categories.Inventory,
            schedule: Schedules.RealTime,
            details: "Notifies when a product catalog fails to process due to the data not being compliant."
        }
    ],
    Warning: [
        {
            id: 12,
            description: "Login to external system failed",
            category: Categories.Connectivity,
            schedule: Schedules.RealTime,
            details: "Alerts when Logicbroker fails to connect to an external system via pre-built connector. The connection will continue to retry, however the warning is sent with full details for visibility to potential issues."
        },
        {
            id: 26,
            description: "Overdue functional acknowledgements",
            scheduled: true,
            category: Categories.Connectivity,
            schedule: Schedules.Configurable,
            details: "Lists orders with overdue 997 acknowledgements (over 24 hours)."
        },
        {
            id: 27,
            description: "Overdue documents",
            scheduled: true,
            category: Categories.Orders,
            schedule: Schedules.Configurable,
            details: "Lists open documents not completed within the expected timeframe. Up to 50 per report."
        },
        {
            id: 45,
            description: "Overdue inventory",
            scheduled: true,
            category: Categories.Inventory,
            schedule: Schedules.Configurable,
            details: "Lists partners who haven't uploaded inventory in over 24 hours (includes Total Items, Matched/Unmatched Items)."
        }
    ]
};

export { EventTypes, Categories };
export type { EventTypeInfo, CategoryValue, ScheduleValue };
